import type { ApiClient } from '../http/apiClient';
import type { IdentityProvider } from '../../auth/identityProvider';
import { buildMetaInfo } from '../model/metaInfo';
import type {
  GrantTenantAccessRequest,
  GrantTenantAccessResponse,
  RevokeTenantAccessRequest,
  RevokeTenantAccessResponse,
  ListTenantSharesRequest,
  ListTenantSharesResponse,
} from '../model/types';

/**
 * TenantAccess client — one method per modeled tenant-sharing operation
 * (GrantTenantAccess, RevokeTenantAccess, ListTenantShares). Paths mirror
 * model/tenantaccess/operations.smithy exactly.
 *
 * Cross-tenant sharing: a team's Vector data is private by default. Granting
 * another tenant READ access lets that tenant read this tenant's data. The
 * grant owner is ALWAYS the caller's own tenant (derived server-side from the
 * resolved store bundle) — a caller cannot grant on behalf of another tenant.
 * Grant/revoke are idempotent. The list response's `body` is a JSON *string* —
 * {@link TenantAccessApi.parseShares} parses it.
 *
 * Calls {@link ApiClient} only. Pure TS — no React.
 *
 * metaInfo: jobCategory is DOCUMENT_MANAGEMENT and requestType is the
 * KNOWN-VALID contextual value LIST_META_INFO — the backend RequestType enum
 * has no tenant-access-specific values and rejects unknown enums with a 500, so
 * we reuse an accepted value rather than inventing one. requestType is
 * contextual only and does not affect sharing behavior.
 */

const PATHS = {
  grant: '/tenant-access/grant',
  revoke: '/tenant-access/revoke',
  list: '/tenant-access/list',
} as const;

/** The default scope granted when none is specified. */
export const DEFAULT_SCOPE = 'READ';

export interface GrantTenantAccessArgs {
  granteeTenantId: string;
  /** Access scope being granted (defaults to READ server-side when omitted). */
  scope?: string;
}

export interface RevokeTenantAccessArgs {
  granteeTenantId: string;
}

/**
 * A parsed tenant-share grant (from the ListTenantShares `body` JSON string):
 * an active outbound grant the caller's tenant has extended to another tenant.
 */
export interface TenantShare {
  granteeTenantId: string;
  /** The owning (caller's) tenant id, when the backend records it. */
  ownerTenantId?: string;
  /** The granted scope (e.g. READ), when recorded. */
  scope?: string;
  /** Grant timestamp, when recorded. */
  grantedAt?: string;
}

export class TenantAccessApi {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly identityProvider: IdentityProvider,
  ) {}

  /**
   * Parses the `body` JSON string of a ListTenantShares response into a
   * normalized {@link TenantShare} list. Tolerates both a bare array and an
   * object with a `shares`/`grants` array, and unknown/missing fields.
   */
  static parseShares(body?: string): TenantShare[] {
    if (!body) return [];
    let raw: unknown;
    try {
      raw = JSON.parse(body);
    } catch {
      return [];
    }
    const obj = (raw ?? {}) as Record<string, unknown>;
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(obj.shares)
        ? (obj.shares as unknown[])
        : Array.isArray(obj.grants)
          ? (obj.grants as unknown[])
          : [];
    return list
      .map((g) => {
        const o = (g ?? {}) as Record<string, unknown>;
        return {
          granteeTenantId:
            (typeof o.granteeTenantId === 'string' && o.granteeTenantId) ||
            (typeof o.granteeTenant === 'string' && o.granteeTenant) ||
            (typeof o.tenantId === 'string' && o.tenantId) ||
            '',
          ...(typeof o.ownerTenantId === 'string'
            ? { ownerTenantId: o.ownerTenantId }
            : {}),
          ...(typeof o.scope === 'string' ? { scope: o.scope } : {}),
          ...(typeof o.grantedAt === 'string'
            ? { grantedAt: o.grantedAt }
            : typeof o.createdAt === 'string'
              ? { grantedAt: o.createdAt }
              : {}),
        };
      })
      .filter((s) => s.granteeTenantId.length > 0);
  }

  async grantTenantAccess(
    args: GrantTenantAccessArgs,
  ): Promise<GrantTenantAccessResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'GrantTenantAccess',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<
      GrantTenantAccessRequest,
      GrantTenantAccessResponse
    >(PATHS.grant, {
      metaInfo,
      granteeTenantId: args.granteeTenantId,
      ...(args.scope ? { scope: args.scope } : {}),
    });
  }

  async revokeTenantAccess(
    args: RevokeTenantAccessArgs,
  ): Promise<RevokeTenantAccessResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'RevokeTenantAccess',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<
      RevokeTenantAccessRequest,
      RevokeTenantAccessResponse
    >(PATHS.revoke, {
      metaInfo,
      granteeTenantId: args.granteeTenantId,
    });
  }

  async listTenantShares(): Promise<ListTenantSharesResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'ListTenantShares',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'LIST_META_INFO',
    });
    return this.apiClient.post<
      ListTenantSharesRequest,
      ListTenantSharesResponse
    >(PATHS.list, {
      metaInfo,
    });
  }
}
